import {
  QueryCache,
  QueryClient,
  QueryClientConfig,
} from '@tanstack/react-query';

import { clearSiteFailure, recordQueryFailure } from './site-failure-store';

export const defaultConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      refetchOnMount: false,
    },
  },
};

let browserQueryClient: QueryClient | undefined;

// A module-level client is a per-process cache, and on the server one process
// serves every studio at once — so a render that seeds it would expose one
// studio's record to another studio's concurrent request. The server therefore
// gets a fresh client per call, which is also what makes the dehydrate/hydrate
// handoff in the route-group layouts safe. The browser keeps one so client-side
// navigation reuses the cache it already filled.
export const getQueryClient = () => {
  if (typeof window === 'undefined') return new QueryClient(defaultConfig);
  browserQueryClient ??= new QueryClient({
    ...defaultConfig,
    // On the cache rather than on each hook: eight hooks in src/state never
    // hand `error` back to their consumer, so a per-hook signal cannot see
    // the failures that matter most. The session check is the one exception:
    // it declares itself in its meta, and its 403 must never blank the site.
    queryCache: new QueryCache({
      // A refetch that fails over data already on screen must not blank a
      // working page: the panel replaces the whole tree, so a proxy blip on
      // a refocused tab would unmount a half-filled checkout. A read with
      // nothing to show still raises it, so a dark or unreachable studio is
      // reported exactly as before.
      onError: (error, query) => {
        if (query.meta?.isSessionCheck || query.state.data !== undefined) return;
        recordQueryFailure(error);
      },
      // Asymmetric on purpose: any read may raise a site-wide failure, but only
      // the tenant probe may retract one. Clearing on any success lets a query
      // that never saw the outage — the session check among them, which is
      // forbidden to set this state — swap the outage panel back for the page
      // tree mid-form, and lets a flaky upstream oscillate the whole site.
      onSuccess: (_data, query) => {
        if (query.meta?.isTenantProbe) clearSiteFailure();
      },
    }),
  });
  return browserQueryClient;
};
